import React, { useState, useRef, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setLogout } from "../store/slices/userSlice";
import {
  FaUser,
  FaChevronDown,
  FaCog,
  FaSignOutAlt,
  FaGlobe,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const menuItems = [];

const loggedInMenuItems = [
  { name: "nav.trackGame", to: "/tracking" },
  { name: "nav.training", to: "/training" },
  { name: "nav.statistics", to: "/statistics" },
  { name: "nav.history", to: "/history" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const userInfo = useSelector((state) => state.user.userInfo);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const navRef = useRef(null);
  const userMenuRef = useRef(null);
  const languageMenuRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Close menus on outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && navRef.current && !navRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setShowUserMenu(false);
      }
      if (
        showLanguageMenu &&
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target)
      ) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, showUserMenu, showLanguageMenu]);

  const handleLogout = () => {
    dispatch(setLogout());
    navigate("/");
    setIsOpen(false);
    setShowUserMenu(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLanguageMenu(false);
  };

  return (
    <nav
      ref={navRef}
      className="fixed top-0 w-full bg-primary text-white shadow-lg z-50 h-16"
    >
      <div className="container h-full mx-auto flex justify-between items-center px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <div id="logo" className="text-xl font-bold text-test">
            99 Darts
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          {menuItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md transition-colors duration-200 ${
                  isActive
                    ? "bg-test text-primary font-medium"
                    : "hover:bg-test/20"
                }`
              }
            >
              {t(item.name)}
            </NavLink>
          ))}

          {isLoggedIn &&
            loggedInMenuItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md transition-colors duration-200 ${
                    isActive
                      ? "bg-test text-primary font-medium"
                      : "hover:bg-test/20"
                  }`
                }
              >
                {t(item.name)}
              </NavLink>
            ))}

          {isLoggedIn ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-test/20 transition-colors duration-200"
              >
                {userInfo?.photoUrl ? (
                  <img
                    src={userInfo.photoUrl}
                    alt="Profile"
                    className="w-8 h-8 rounded-full object-cover border-2 border-test/20"
                  />
                ) : (
                  <div className="bg-test/20 p-1.5 rounded-full">
                    <FaUser className="w-4 h-4 text-test" />
                  </div>
                )}
                <span className="text-sm font-medium max-w-[150px] truncate">
                  {userInfo?.username || userInfo?.email}
                </span>
                <FaChevronDown
                  className={`w-3 h-3 transition-transform duration-200 ${
                    showUserMenu ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-secondary rounded-lg shadow-lg py-1 border border-primary/20">
                  <div className="px-4 py-2 border-b border-primary/20">
                    {userInfo?.photoUrl ? (
                      <div className="flex items-center space-x-3">
                        <img
                          src={userInfo.photoUrl}
                          alt="Profile"
                          className="w-10 h-10 rounded-full object-cover border-2 border-test/20"
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-white truncate max-w-[120px]">
                            {userInfo?.username || userInfo?.email}
                          </span>
                          <span className="text-xs text-white/60 truncate max-w-[120px]">
                            {userInfo?.email}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-3">
                        <div className="bg-test/20 p-2 rounded-full">
                          <FaUser className="w-5 h-5 text-test" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-white truncate max-w-[120px]">
                            {userInfo?.username || userInfo?.email}
                          </span>
                          <span className="text-xs text-white/60 truncate max-w-[120px]">
                            {userInfo?.email}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <Link
                    to="/settings"
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-primary/50 transition-colors duration-200"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <FaCog className="w-4 h-4" />
                    <span>{t("nav.settings")}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-primary/50 transition-colors duration-200 w-full"
                  >
                    <FaSignOutAlt className="w-4 h-4" />
                    <span>{t("nav.logout")}</span>
                  </button>
                  {/* Language Switcher */}
                  <div className="relative" ref={languageMenuRef}>
                    <button
                      onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-test/20 transition-colors duration-200"
                    >
                      <FaGlobe className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {t("common.language")}
                      </span>
                      <FaChevronDown
                        className={`w-3 h-3 transition-transform duration-200 ${
                          showLanguageMenu ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showLanguageMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-secondary rounded-lg shadow-lg py-1 border border-primary/20">
                        <button
                          onClick={() => changeLanguage("de")}
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-primary/50 transition-colors duration-200 w-full"
                        >
                          <span>{t("common.de")}</span>
                        </button>
                        <button
                          onClick={() => changeLanguage("en")}
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-white hover:bg-primary/50 transition-colors duration-200 w-full"
                        >
                          <span>{t("common.en")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="px-4 py-2 bg-test text-primary rounded-md hover:bg-test/90 transition-colors duration-200 text-sm font-medium"
            >
              {t("nav.login")}
            </Link>
          )}
        </div>

        {/* Hamburger Menu (Mobile) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="md:hidden focus:outline-none"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-primary shadow-lg">
          <div className="px-6 pt-4 pb-6 space-y-3">
            {menuItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md transition-colors duration-200 ${
                    isActive
                      ? "bg-test text-primary font-medium"
                      : "hover:bg-test/20"
                  }`
                }
                onClick={() => setIsOpen(false)}
              >
                {t(item.name)}
              </NavLink>
            ))}

            {isLoggedIn &&
              loggedInMenuItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.to}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-md transition-colors duration-200 ${
                      isActive
                        ? "bg-test text-primary font-medium"
                        : "hover:bg-test/20"
                    }`
                  }
                  onClick={() => setIsOpen(false)}
                >
                  {t(item.name)}
                </NavLink>
              ))}

            {/* Mobile Language Switcher */}
            <div className="px-3 py-2">
              <div className="flex items-center space-x-2">
                <FaGlobe className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {t("common.language")}
                </span>
              </div>
              <div className="mt-2 space-y-2">
                <button
                  onClick={() => changeLanguage("de")}
                  className="block w-full px-3 py-2 text-sm text-white hover:bg-test/20 rounded-md transition-colors duration-200 text-left"
                >
                  {t("common.de")}
                </button>
                <button
                  onClick={() => changeLanguage("en")}
                  className="block w-full px-3 py-2 text-sm text-white hover:bg-test/20 rounded-md transition-colors duration-200 text-left"
                >
                  {t("common.en")}
                </button>
              </div>
            </div>

            <hr className="border-test/30 my-3" />

            {isLoggedIn ? (
              <div className="space-y-3">
                <div className="flex items-center space-x-2 px-3 py-2">
                  {userInfo?.photoUrl ? (
                    <img
                      src={userInfo.photoUrl}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover border-2 border-test/20"
                    />
                  ) : (
                    <div className="bg-test/20 p-1.5 rounded-full">
                      <FaUser className="w-4 h-4 text-test" />
                    </div>
                  )}
                  <span className="text-sm font-medium">
                    {userInfo?.username || userInfo?.email}
                  </span>
                </div>
                <Link
                  to="/settings"
                  className="flex items-center space-x-2 px-3 py-2 text-sm hover:bg-test/20 rounded-md transition-colors duration-200"
                  onClick={() => setIsOpen(false)}
                >
                  <FaCog className="w-4 h-4" />
                  <span>{t("nav.settings")}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 w-full px-3 py-2 text-sm hover:bg-test/20 rounded-md transition-colors duration-200"
                >
                  <FaSignOutAlt className="w-4 h-4" />
                  <span>{t("nav.logout")}</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="block px-3 py-2 bg-test text-primary rounded-md hover:bg-test/90 transition-colors duration-200 text-sm font-medium text-center"
                onClick={() => setIsOpen(false)}
              >
                {t("nav.login")}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
